import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";
import { EnvironmentBanner, EnvironmentChip, EnvironmentWatermark } from "@/components/EnvironmentBanner";
import BuyerProtectionPanel, { UnlockedSellerCard } from "@/components/BuyerProtectionPanel";
import { toast } from "sonner";
import {
  ShieldCheck, MapPin, Fuel, Gauge, Cog, Download, FileText, ScanLine, HandshakeIcon,
  Sparkles, CheckCircle2, Circle, Award, TrendingUp, TrendingDown, Minus,
  Car as CarIcon, Wrench, ClipboardCheck, LineChart, History, IdCard, Timer,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const FALLBACK_IMG = "/catalog/pending-verification.svg";
const mask = (s) => (!s ? null : s.length <= 4 ? "•".repeat(s.length) : "•".repeat(Math.max(0, s.length - 4)) + s.slice(-4));

const RATING = {
  excellent:       { label: "Excellent",       dot: "bg-emerald-500",  text: "text-emerald-700"  },
  good:            { label: "Good",            dot: "bg-lime-500",     text: "text-lime-700"     },
  fair:            { label: "Fair",            dot: "bg-amber-500",    text: "text-amber-700"    },
  needs_attention: { label: "Needs Attention", dot: "bg-red-500",      text: "text-red-700"      },
  not_assessed:    { label: "Not Assessed",    dot: "bg-slate-300",    text: "text-slate-500"    },
};

const TIER_COLORS = {
  none:     "bg-slate-500",
  bronze:   "bg-amber-700",
  silver:   "bg-slate-400",
  gold:     "bg-amber-500",
  platinum: "bg-teal-600",
  diamond:  "bg-sky-600",
};

const MECH_ROWS = [
  ["engine", "Engine"], ["transmission", "Transmission"], ["suspension", "Suspension"],
  ["brakes", "Brakes"], ["tyres", "Tyres"], ["battery", "Battery"],
  ["cooling", "Cooling System"], ["steering", "Steering"], ["aircon", "Air Conditioning"],
  ["electrical", "Electrical System"],
];

const CHECKLIST_TO_VERIFIED = {
  vin: "VIN Verified",
  engine_number: "Engine Number Verified",
  zimra_duty: "Import & Duty Documentation Verified",
  zrp_clearance: "Police Clearance Verified",
  registration_book: "Registration & Ownership Verified",
  mileage: "Odometer Verified",
  engine_health: "Mechanical Inspection Completed",
  accident_history: "Structural Inspection Completed",
  tires: "Roadworthiness Checked",
  interior: "Interior & Electronics Checked",
};

const PRICE_RATING_META = {
  excellent:    { label: "Excellent Price", Icon: TrendingDown, color: "text-emerald-700" },
  fair:         { label: "Fair Price",       Icon: Minus,         color: "text-slate-700"  },
  above_market: { label: "Above Market",     Icon: TrendingUp,    color: "text-amber-700"  },
  unknown:      { label: "Not Assessed",     Icon: Minus,         color: "text-slate-400"  },
};

const Row = ({ k, v }) => v == null || v === "" ? null : (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm border-b border-border last:border-0">
    <div className="text-muted-foreground">{k}</div>
    <div className="font-medium">{String(v)}</div>
  </div>
);

const SectionCard = ({ title, Icon, children, defaultOpen = false }) => (
  <Accordion type="single" collapsible defaultValue={defaultOpen ? title : undefined}>
    <AccordionItem value={title} className="border border-border bg-card rounded-none">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/40">
        <div className="flex items-center gap-2 text-left">
          <Icon className="h-4 w-4 text-primary" />
          <span className="display text-base">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        {children}
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export default function VehicleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [passport, setPassport] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [escrowOpen, setEscrowOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("paynow_ecocash");
  const [submitting, setSubmitting] = useState(false);
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    (async () => {
      try {
        const [vr, pr, gr] = await Promise.all([
          api.get(`/vehicles/${id}`),
          api.get(`/reports/vehicle/${id}/passport`),
          api.get(`/escrow/gateways`),
        ]);
        setV(vr.data);
        setPassport(pr.data);
        setGateways(gr.data);
      } catch { toast.error("Vehicle not found"); }
    })();
  }, [id]);

  const isVerified = v?.verification_status === "verified";

  useEffect(() => {
    if (!isVerified || !v) return;
    (async () => {
      setAiLoading(true);
      try {
        const r = await api.get(`/reports/vehicle/${id}/ai-summary`);
        setAiSummary(r.data.summary);
      } catch {} finally { setAiLoading(false); }
    })();
  }, [isVerified, id, v]);

  const activeCert = passport?.certificates?.find(c => c.status === "valid");
  const trust = passport?.trust;
  const badge = passport?.badge;
  const events = passport?.events || [];
  const photos = (v?.photos && v.photos.length > 0) ? v.photos : null;

  const verifiedChecklist = useMemo(() => {
    if (!v?.inspection?.checklist) return [];
    return v.inspection.checklist.map((c) => ({
      label: CHECKLIST_TO_VERIFIED[c.key] || c.label,
      result: c.result,
    }));
  }, [v]);

  const submitEscrow = async () => {
    if (!user) { toast("Sign in to place an escrow deposit"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/escrow", { vehicle_id: id, amount_usd: parseFloat(amount), payment_method: method });
      toast.success(`Deposit committed. Ref ${r.data.payment_reference}`);
      setEscrowOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Escrow failed");
    } finally { setSubmitting(false); }
  };

  if (!v) return (<div><Navbar /><div className="p-10">Loading…</div></div>);

  const priceRating = PRICE_RATING_META[v.price_rating || "unknown"];

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <Link to="/marketplace" className="text-sm text-muted-foreground hover:text-primary">← Back to marketplace</Link>

        {/* Environment banner — Demonstration / Sandbox / Pilot (hidden for production) */}
        <div className="mt-4" data-testid="vehicle-demo-notice">
          <EnvironmentBanner record={v} />
        </div>

        <div className="grid lg:grid-cols-12 gap-8 mt-4">
          {/* ---------- Left: Gallery ---------- */}
          <div className="lg:col-span-7">
            <div className="relative border border-border bg-secondary overflow-hidden">
              <img
                src={photos ? `${API_BASE}/files/${photos[0]}?v=large` : (v.catalog_photo_url || FALLBACK_IMG)}
                alt={`${v.year || ""} ${v.make} ${v.model}`.trim()}
                className="w-full h-[480px] object-cover"
                loading="eager"
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
              />
              {/* Environment watermark — non-production only */}
              <EnvironmentWatermark record={v} testid="vehicle-demo-watermark" />
              {v.photo_source === "pending_verification" ? (
                <div className="absolute bottom-4 left-4 mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-black/80 text-amber-300 rounded-sm border border-amber-500/40" data-testid="vehicle-pending-verification">
                  Image Pending Verification · No stock photograph will be shown until an inspector's photo lands
                </div>
              ) : v.photo_source && v.photo_source !== "actual" ? (
                <div className="absolute bottom-4 left-4 mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-black/70 text-white rounded-sm" data-testid="vehicle-illustrative">
                  Illustrative image — actual photos pending inspection
                </div>
              ) : null}
              {/* THOM Verified stamp — ONLY when NOT a demo AND genuinely verified */}
              {!v.is_demo && isVerified && (
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <div className="bg-white/95 backdrop-blur-sm rounded-full p-1 shadow-md">
                    <ThomVerifiedStamp
                      size={72}
                      verificationStatus="verified"
                      certificateId={activeCert?.certificate_number}
                      trustScore={trust?.score}
                      clickable={!!activeCert?.certificate_number}
                      showTooltip
                    />
                  </div>
                  {badge?.tier && badge.tier !== "none" && (
                    <div className={`mono text-[11px] uppercase tracking-widest px-2 py-1 text-white ${TIER_COLORS[badge.tier]}`}>
                      {badge.label} Badge
                    </div>
                  )}
                </div>
              )}
              {/* Demo pill on hero image */}
              {v.is_demo && (
                <div className="absolute top-4 left-4 mono text-[11px] uppercase tracking-widest px-3 py-1.5 bg-amber-500 text-black rounded-sm border border-amber-700 shadow-md">Demo Listing · Illustrative Example</div>
              )}
            </div>
            {photos && photos.length > 1 && (
              <div className="grid grid-cols-6 gap-2 mt-2">
                {photos.slice(0, 6).map((p) => (
                  <img key={p} src={`${API_BASE}/files/${p}?v=thumb`} className="aspect-square object-cover border border-border" alt="" />
                ))}
              </div>
            )}
          </div>

          {/* ---------- Right: Title + Trust + CTA ---------- */}
          <div className="lg:col-span-5">
            <div className="kicker mb-2 flex items-center gap-2">
              <span>Listing</span>
              <EnvironmentChip record={v} testid="vehicle-env-chip" />
            </div>
            <h1 className="display text-4xl leading-tight" data-testid="vehicle-title">
              {v.year} {v.make} {v.model} {v.variant && <span className="text-2xl font-semibold text-muted-foreground">{v.variant}</span>}
            </h1>

            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-3xl font-semibold text-primary mono">${Number(v.price_usd).toLocaleString()}</div>
              {v.price_rating && v.price_rating !== "unknown" && (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${priceRating.color}`}>
                  <priceRating.Icon className="h-3.5 w-3.5" /> {priceRating.label}
                </span>
              )}
            </div>
            {v.estimated_market_value && (
              <div className="text-xs text-muted-foreground mt-1">
                Est. market value <span className="mono">${Number(v.estimated_market_value).toLocaleString()}</span>
                {v.fair_market_low && v.fair_market_high && (
                  <> · fair range <span className="mono">${Number(v.fair_market_low).toLocaleString()}–${Number(v.fair_market_high).toLocaleString()}</span></>
                )}
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> {v.city}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><Gauge className="h-4 w-4" /> {v.mileage_km?.toLocaleString()} km</div>
              <div className="flex items-center gap-2 text-muted-foreground"><Cog className="h-4 w-4" /> {v.transmission}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><Fuel className="h-4 w-4" /> {v.fuel}</div>
            </div>

            {isVerified && trust && (
              <div className="mt-6 p-5 border border-border bg-card flex items-start gap-5" data-testid="vehicle-verified-panel">
                <ThomVerifiedStamp
                  size={112}
                  verificationStatus="verified"
                  certificateId={activeCert?.certificate_number}
                  trustScore={trust.score}
                  rating={trust.band}
                  verificationDate={activeCert?.issued_at}
                  expiryDate={activeCert?.expires_at}
                  clickable={!!activeCert?.certificate_number}
                  showTooltip
                />
                <div className="flex-1">
                  <div className="kicker">THOM Verify™ Trust Score</div>
                  <div className="flex items-baseline gap-3">
                    <div className="display text-5xl text-primary" data-testid="trust-score-value">{trust.score}</div>
                    <div className="text-sm text-muted-foreground">{trust.band}</div>
                  </div>
                  {badge?.tier && badge.tier !== "none" && (
                    <div className="mt-2 inline-flex items-center gap-1 text-xs mono uppercase tracking-widest">
                      <Award className="h-3 w-3 text-primary" /> {badge.label} Trust Badge
                    </div>
                  )}
                  {activeCert && (
                    <div className="mt-2 text-xs mono text-muted-foreground">
                      Certificate: {activeCert.certificate_number} · Issued {new Date(activeCert.issued_at).toLocaleDateString()}
                      {activeCert.expires_at && <> · Valid until {new Date(activeCert.expires_at).toLocaleDateString()}</>}
                    </div>
                  )}
                  <div className="mt-2">
                    <Link to={`/passport/vehicle/${v.id}`} className="text-primary text-xs hover:underline" data-testid="link-digital-passport">
                      View Digital Passport →
                    </Link>
                  </div>
                </div>
              </div>
            )}
            {isVerified && trust?.breakdown && (
              <div className="mt-4 p-5 border border-border bg-card" data-testid="trust-breakdown-panel">
                <div className="kicker mb-3">Trust Score Breakdown — 4-category verification</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    {k: "mechanical",    label: "Mechanical & Technical"},
                    {k: "identity",      label: "Vehicle Identity"},
                    {k: "compliance",    label: "Legal & Compliance"},
                    {k: "fraud_risk",    label: "Fraud Risk Assessment"},
                    {k: "documentation", label: "Documentation"},
                    {k: "recency",       label: "Inspection Recency"},
                  ].map(row => (
                    <div key={row.k}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className="mono">{trust.breakdown[row.k] ?? 0}%</span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${trust.breakdown[row.k] ?? 0}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                {v.fraud_risk?.rating && (
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <div>
                      <div className="kicker">Vehicle Fraud Risk Rating</div>
                      <div className={`display text-2xl mt-1 ${
                        ["Very Low","Low"].includes(v.fraud_risk.rating) ? "text-primary" :
                        v.fraud_risk.rating === "Moderate" ? "text-amber-600" : "text-red-600"
                      }`} data-testid="fraud-risk-rating">{v.fraud_risk.rating}</div>
                    </div>
                    <div className="text-xs text-muted-foreground max-w-md text-right">
                      {v.fraud_risk.reasons?.slice(0, 2).join(" ")}
                    </div>
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-border/50 text-[10px] mono text-muted-foreground leading-relaxed">
                  Verification results depend on authorised partner integrations available at inspection time. Coverage of theft, cross-border, finance-lien and recall databases varies by jurisdiction and participating organisations.
                </div>
              </div>
            )}
            {isVerified && v.trust_indicators?.length > 0 && (
              <div className="mt-4 p-5 border border-border bg-card grid sm:grid-cols-2 gap-2" data-testid="vehicle-trust-indicators">
                {v.trust_indicators.map(ti => (
                  <div key={ti.key} className={`flex items-center gap-2 text-sm ${ti.ok ? "" : "text-muted-foreground"}`}>
                    {ti.ok ? <span className="mono text-primary">✓</span> : <span className="mono text-muted-foreground">…</span>}
                    <span>{ti.label}</span>
                    {ti.partner && <span className="ml-auto text-[10px] mono uppercase tracking-widest text-muted-foreground border px-1.5 py-0.5 rounded-sm">Partner data</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 grid gap-2">
              {isVerified ? (
                <>
                  <a href={`${API_BASE}/reports/vehicle/${id}.html`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="view-html-report"><FileText className="h-4 w-4" /> Interactive HTML Report</a>
                  <a href={`${API_BASE}/reports/vehicle/${id}.pdf`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="download-pdf-report"><Download className="h-4 w-4" /> Download PDF Report</a>
                  {activeCert && (
                    <Link to={`/verify/${activeCert.certificate_number}`} className="btn-outline" data-testid="view-certificate"><ScanLine className="h-4 w-4" /> Verify Certificate</Link>
                  )}
                  <Dialog open={escrowOpen} onOpenChange={setEscrowOpen}>
                    <DialogTrigger asChild>
                      <button className="btn-accent mt-2" data-testid="open-escrow-dialog"><HandshakeIcon className="h-4 w-4" /> Commit Escrow Deposit</button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>THOM Escrow™ Deposit</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div><Label>Deposit amount (USD)</Label><Input type="number" min="1" placeholder="e.g. 500" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="escrow-amount-input" /></div>
                        <div>
                          <Label>Payment method</Label>
                          <Select value={method} onValueChange={setMethod}>
                            <SelectTrigger data-testid="escrow-method-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{gateways.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">Funds are held by THOM Verify™ and released only when you accept the vehicle.</p>
                      </div>
                      <DialogFooter>
                        <Button onClick={submitEscrow} disabled={submitting || !amount} data-testid="escrow-confirm-btn">
                          {submitting ? "Processing…" : "Commit Deposit"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <div className="p-4 border border-amber-500/40 bg-amber-500/5 text-sm">
                  This vehicle is <b>{v.verification_status}</b>. Escrow is only available on THOM Verified™ listings.
                </div>
              )}
            </div>

            <BuyerProtectionPanel vehicle={v} onUnlocked={() => window.location.reload()} />
            <UnlockedSellerCard vehicle={v} />

            {v.seller && !v.seller.contact_locked && (
              <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4">
                Sold by <span className="text-foreground font-medium">{v.seller.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* ---------- AI Executive Summary ---------- */}
        {isVerified && (
          <div className="mt-10 p-6 border border-primary/30 bg-primary/[0.04]">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <div className="kicker text-primary">AI Executive Summary</div>
            </div>
            {aiLoading && !aiSummary ? (
              <div className="text-sm text-muted-foreground">Generating summary…</div>
            ) : (
              <p className="text-sm leading-relaxed max-w-4xl" data-testid="ai-summary-text">{aiSummary || "Summary not available yet."}</p>
            )}
            <p className="text-[11px] mono text-muted-foreground mt-3">Generated by THOM Verify™ AI · reviewed for cautious wording · not a guarantee.</p>
          </div>
        )}

        {/* ---------- Verification Status Card ---------- */}
        {isVerified && v.inspection && (
          <div className="mt-10 border border-border bg-card p-6">
            <div className="flex items-baseline justify-between mb-4 gap-4">
              <div>
                <div className="kicker">Verification Status</div>
                <h3 className="display text-2xl">All checks complete &amp; digitally certified.</h3>
              </div>
              {activeCert && (
                <div className="text-right">
                  <div className="mono text-xs text-muted-foreground">Certificate valid until</div>
                  <div className="font-semibold">{new Date(activeCert.expires_at).toLocaleDateString()}</div>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
              {verifiedChecklist.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {it.result === "pass" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : it.result === "fail" ? (
                    <Circle className="h-4 w-4 text-red-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-slate-300" />
                  )}
                  <span className={it.result === "pass" ? "" : "text-muted-foreground"}>{it.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>Digital Certificate Issued</span>
              </div>
            </div>
          </div>
        )}

        {/* ---------- Executive Specification Panel ---------- */}
        <div className="mt-10 grid gap-3">
          <SectionCard title="Vehicle Identification" Icon={IdCard} defaultOpen>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row k="Make" v={v.make} />
                <Row k="Model" v={v.model} />
                <Row k="Variant" v={v.variant} />
                <Row k="Year of Manufacture" v={v.year} />
                <Row k="Model Year" v={v.model_year} />
                <Row k="Country of Manufacture" v={v.country_of_manufacture} />
                <Row k="Category" v={v.category && v.category.toUpperCase()} />
                <Row k="Body Style" v={v.body_style} />
              </div>
              <div>
                <Row k="Colour" v={v.colour} />
                <Row k="VIN / Chassis" v={mask(v.vin)} />
                <Row k="Engine Number" v={mask(v.engine_number)} />
                <Row k="Registration Number" v={v.registration_number} />
                <Row k="Registration Province" v={v.registration_province} />
                <Row k="Doors" v={v.doors} />
                <Row k="Seating Capacity" v={v.seats} />
                <Row k="Listing ID" v={v.id} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Technical Specifications" Icon={CarIcon}>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row k="Engine Capacity" v={v.engine_cc ? `${v.engine_cc} cc` : null} />
                <Row k="Engine Type" v={v.engine_type} />
                <Row k="Fuel Type" v={v.fuel} />
                <Row k="Transmission" v={v.transmission} />
                <Row k="Drivetrain" v={v.drivetrain && v.drivetrain.toUpperCase()} />
              </div>
              <div>
                <Row k="Power" v={v.power_kw ? `${v.power_kw} kW${v.power_hp ? ` (${v.power_hp} HP)` : ""}` : (v.power_hp ? `${v.power_hp} HP` : null)} />
                <Row k="Torque" v={v.torque_nm ? `${v.torque_nm} Nm` : null} />
                <Row k="Fuel Consumption" v={v.fuel_consumption_lp100 ? `${v.fuel_consumption_lp100} L/100km` : null} />
                <Row k="Fuel Tank" v={v.fuel_tank_l ? `${v.fuel_tank_l} L` : null} />
                <Row k="Emissions Standard" v={v.emissions_standard} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Vehicle History" Icon={History}>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row k="First Registration" v={v.first_registration_date} />
                <Row k="Import Date" v={v.import_date} />
                <Row k="Country of Origin" v={v.country_of_origin} />
                <Row k="Port of Entry" v={v.port_of_entry} />
                <Row k="Duty Status" v={v.duty_status && v.duty_status.toUpperCase()} />
              </div>
              <div>
                <Row k="Previous Owners" v={v.previous_owners} />
                <Row k="Current Owner Since" v={v.current_owner_since} />
                <Row k="Service History" v={v.service_history} />
                <Row k="Accident History" v={v.accident_history} />
                <Row k="Insurance Write-off" v={v.insurance_writeoff} />
                <Row k="Flood Damage Status" v={v.flood_damage} />
              </div>
            </div>
            <div className="mt-4 text-[11px] text-muted-foreground">
              Theft &amp; finance/lien status subject to authorised data access — shown on the full report where confirmed.
            </div>
          </SectionCard>

          <SectionCard title="Mechanical Status" Icon={Wrench}>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
              {MECH_ROWS.map(([k, label]) => {
                const rating = v.mechanical?.[k] || "not_assessed";
                const meta = RATING[rating];
                return (
                  <div key={k} className="flex items-center justify-between border-b border-border py-2 text-sm">
                    <span>{label}</span>
                    <span className={`inline-flex items-center gap-2 ${meta.text}`}>
                      <span className={`h-2 w-2 rounded-full ${meta.dot}`} /> {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard title="Market Information" Icon={LineChart}>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row k="Asking Price" v={`USD ${Number(v.price_usd).toLocaleString()}`} />
                <Row k="Estimated Market Value" v={v.estimated_market_value ? `USD ${Number(v.estimated_market_value).toLocaleString()}` : null} />
                <Row k="Fair Market Range" v={v.fair_market_low && v.fair_market_high ? `USD ${Number(v.fair_market_low).toLocaleString()} – ${Number(v.fair_market_high).toLocaleString()}` : null} />
              </div>
              <div>
                <Row k="Insurance Value (Est.)" v={v.estimated_insurance_value ? `USD ${Number(v.estimated_insurance_value).toLocaleString()}` : null} />
                <Row k="Price Rating" v={priceRating.label} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Trust Indicators & Certificate" Icon={ClipboardCheck}>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row k="Trust Score" v={trust?.score != null ? `${trust.score} / 100` : "—"} />
                <Row k="Band" v={trust?.band} />
                <Row k="Badge Tier" v={badge?.label} />
                <Row k="Overall Recommendation" v={isVerified ? "PROCEED — subject to buyer due diligence" : "Not yet verified"} />
              </div>
              <div>
                <Row k="Certificate #" v={activeCert?.certificate_number} />
                <Row k="Issued" v={activeCert?.issued_at ? new Date(activeCert.issued_at).toLocaleDateString() : "—"} />
                <Row k="Valid Until" v={activeCert?.expires_at ? new Date(activeCert.expires_at).toLocaleDateString() : "—"} />
                <Row k="Inspection Centre" v={v.city ? `THOM Verify™ · ${v.city}` : "—"} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="THOM Digital Passport™ — asset lifetime history" Icon={Timer}>
            {events.length === 0 ? (
              <div className="text-sm text-muted-foreground">No events recorded yet.</div>
            ) : (
              <ol className="relative border-l border-border ml-3">
                {events.map((e) => (
                  <li key={e.id} className="ml-4 py-3">
                    <div className="absolute -left-1.5 h-3 w-3 rounded-full bg-primary" />
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium">{e.title}</div>
                      <div className="mono text-[11px] text-muted-foreground">{new Date(e.ts).toLocaleString()}</div>
                    </div>
                    {e.detail && <div className="text-sm text-muted-foreground">{e.detail}</div>}
                    <Badge variant="outline" className="mt-1 mono text-[10px]">{e.kind.replace(/_/g, " ")}</Badge>
                  </li>
                ))}
              </ol>
            )}
          </SectionCard>
        </div>

        {v.description && (
          <div className="mt-10">
            <div className="kicker mb-2">Seller description</div>
            <p className="text-sm max-w-3xl leading-relaxed whitespace-pre-line">{v.description}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
